import type {
  QueryRequest,
  QueryRequestFilters,
} from '@moment/horizon-data-access/platform'

import { createQueryRequest } from '@moment/horizon-data-access'

import { parseClientColumnId } from '@/shared/sorm/cross-entity-columns'
import { displayLabelFieldFor } from '@/shared/sorm/display-label-fields'
import { normalizeDashedSearchTerm } from '@/shared/sorm/normalize-dashed-search-term'
import { SORM_KNOWN_SET_FIELD_NAMES } from '@/shared/sorm/set-fields'

export const FILTER_OPTIONS_LIMIT = 100

/** Distinct values fetched per page as the filter menu scrolls. */
export const FILTER_OPTIONS_PAGE_SIZE = 100

/** SORM's `MaxQueryLimit`: the most rows one request can return. */
export const SORM_MAX_QUERY_LIMIT = 10_000

export type FilterOption = { value: string; label: string }

const fieldRef = (entity: string, field: string) => ({ entity, field })

/**
 * Builds a SORM query to fetch distinct values for a filter dropdown.
 * For cross-entity columns (e.g. "Security.sector" on a Lot root),
 * parses the column id into entity + field.
 */
function mergeFilters(
  parts: QueryRequestFilters[],
): QueryRequestFilters | undefined {
  if (parts.length === 0) return undefined
  if (parts.length === 1) return parts[0]
  return { and: parts }
}

/**
 * Whether a filter column holds a set. The schema type decides; the known-name
 * list covers set fields whose schema omits `type: 'set'`.
 */
export function isSetFilterField({
  rootEntity,
  fieldName,
  schemaFieldType,
}: {
  rootEntity: string
  fieldName: string
  schemaFieldType?: string
}): boolean {
  if (schemaFieldType === 'set') return true
  const { field } = parseClientColumnId({ columnId: fieldName, rootEntity })
  return SORM_KNOWN_SET_FIELD_NAMES.has(field)
}

interface FilterOptionsQueryShape {
  isSetField?: boolean
  /** A set field groups by element, which has no sort to page by, so it is never paged. */
  paged?: boolean
}

export function buildFilterOptionsQueryPayload(
  rootEntity: string,
  fieldName: string,
  search?: string,
  context?: string,
  baseFilters?: QueryRequestFilters,
  shape: FilterOptionsQueryShape = {},
): QueryRequest {
  const { entity, field } = parseClientColumnId({
    columnId: fieldName,
    rootEntity,
  })
  const ref = fieldRef(entity, field)
  const labelRef = displayLabelFieldFor({ columnId: fieldName, rootEntity })
  const isSetField =
    shape.isSetField ?? isSetFilterField({ rootEntity, fieldName })
  const paged = shape.paged ?? !isSetField

  const filterParts: QueryRequestFilters[] = []
  if (baseFilters) filterParts.push(baseFilters)
  let normalizedSearch: string | undefined
  if (search != null && search !== '') {
    // Dash stripping targets dashless custodian/lot IDs; set elements are free-form.
    normalizedSearch = isSetField ? search : normalizeDashedSearchTerm(search)
  }
  if (normalizedSearch != null && normalizedSearch !== '') {
    // SORM rejects scalar operators on set fields; elements need the set LIKE.
    const valueSearch = {
      [`${entity}.${field}`]: isSetField
        ? { contains_like_case_insensitive: normalizedSearch }
        : { includes_case_insensitive: normalizedSearch },
    }
    const labelSearch = labelRef
      ? {
          [`${labelRef.entity}.${labelRef.field}`]: {
            includes_case_insensitive: search,
          },
        }
      : undefined
    filterParts.push(
      labelSearch ? { or: [valueSearch, labelSearch] } : valueSearch,
    )
  }

  const filters = mergeFilters(filterParts)
  const fields = labelRef ? [ref, labelRef] : [ref]

  return createQueryRequest({
    root_entity: rootEntity,
    groups: [{ key: ref }],
    fields,
    omit_root_total_count: true,
    ...(paged
      ? {
          limit: FILTER_OPTIONS_PAGE_SIZE,
          offset: 0,
          // Offset paging needs a stable order; grouped queries have none by default.
          sort: [{ ...ref, direction: 'asc' as const }],
        }
      : { limit: SORM_MAX_QUERY_LIMIT }),
    ...(context != null ? { context } : {}),
    ...(filters != null ? { filters } : {}),
  })
}

/** A set-field search selects rows, so SORM returns every element of those rows. */
export function narrowSetFieldOptions({
  options,
  isSetField,
  search,
}: {
  options: FilterOption[]
  isSetField: boolean
  search?: string
}): FilterOption[] {
  if (search == null || search === '' || !isSetField) return options
  const needle = search.toLowerCase()
  return options.filter((opt) => opt.value.toLowerCase().includes(needle))
}

function getFieldValueFromRow(
  row: Record<string, unknown>,
  rootEntity: string,
  fieldName: string,
): string | null {
  if (row[fieldName] != null && typeof row[fieldName] === 'string') {
    return row[fieldName]
  }
  const entityField = `${rootEntity}.${fieldName}`
  if (row[entityField] != null && typeof row[entityField] === 'string') {
    return row[entityField]
  }
  // Cross-entity grouped response may key by bare field (e.g. "sector" for Security.sector)
  const { field } = parseClientColumnId({ columnId: fieldName, rootEntity })
  if (
    field !== fieldName &&
    row[field] != null &&
    typeof row[field] === 'string'
  ) {
    return row[field]
  }
  const fallbackValue = row[fieldName] ?? row[entityField] ?? row[field]
  if (fallbackValue != null) {
    return String(fallbackValue)
  }
  return null
}

/**
 * Parses SORM query response into filter options (value + label).
 * Response data rows may key the field as "fieldName" or "Entity.fieldName".
 */
export function parseFilterOptionsResponse(
  data: Record<string, unknown>[] | undefined,
  rootEntity: string,
  fieldName: string,
): FilterOption[] {
  if (!Array.isArray(data) || data.length === 0) return []
  const labelRef = displayLabelFieldFor({ columnId: fieldName, rootEntity })
  const labelColumnId = labelRef
    ? `${labelRef.entity}.${labelRef.field}`
    : undefined
  const seen = new Set<string>()
  const options: FilterOption[] = []
  for (const row of data) {
    if (!row || typeof row !== 'object') continue
    const value = getFieldValueFromRow(
      row as Record<string, unknown>,
      rootEntity,
      fieldName,
    )
    if (value != null && value !== '' && !seen.has(value)) {
      seen.add(value)
      const label = labelColumnId
        ? getFieldValueFromRow(row, rootEntity, labelColumnId)
        : null
      options.push({ value, label: label || value })
    }
  }
  return options
}
